import {
  $createLineBreakNode,
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $isElementNode,
  $isRootNode,
  LexicalNode,
} from 'lexical';
import { Token, tokenize } from '../../formula-engine';
import {
  FieldRefTarget,
  isFormulaRecord,
  KPI_PERIOD_TARGETS,
  KPI_SECTION_TARGETS,
} from '../../util/formula-vocabulary';
import {
  $createFieldChipNode,
  $isFieldChipNode,
  FieldChipNode,
  FieldChipVariant,
  fieldChipText,
  fieldChipVariant,
} from './FieldChipNode';

export interface FormulaSerialized {
  /** What the user sees: chips as [Display.Path]. Drives validation and the error caret. */
  display: string;
  /** Chips as [canonical.path]; hand-typed refs pass through untouched. Drives save. */
  canonical: string;
}

/** What a stored ref needs to become a chip: the canonical path the chip carries, and its look. */
export type ChipTarget = Pick<FieldRefTarget, 'path' | 'displayPath' | 'type' | 'list' | 'kpiToken'>;

/**
 * Path → chip target, over the referenceable vocabulary plus the KPI
 * period/scenario tokens, so a stored KPI([kpiData.7], [Quarter], [actual])
 * re-chips all three arguments. A field's legacy path (the unprefixed row key
 * formulas stored before records use) maps to the same chip, so those load
 * as the record chip and re-save in the new form. Records never chip. Real
 * fields are added last and win a (never-expected) path collision with a
 * token, and canonical paths win one with a legacy alias.
 */
export function chipTargets(fields: FieldRefTarget[]): Map<string, ChipTarget> {
  const targets = new Map<string, ChipTarget>();
  for (const token of [...KPI_PERIOD_TARGETS, ...KPI_SECTION_TARGETS]) {
    targets.set(token.path, token);
  }
  const chippable = fields.filter((field) => !isFormulaRecord(field));
  for (const field of chippable) {
    if (field.legacyPath !== undefined) {
      targets.set(field.legacyPath, field);
    }
  }
  for (const field of chippable) {
    targets.set(field.path, field);
  }
  return targets;
}

function serialize(refText: (chip: FieldChipNode) => string): string {
  let out = '';
  $getRoot()
    .getChildren()
    .forEach((block, i) => {
      if (i > 0) {
        out += '\n';
      }
      if (!$isElementNode(block)) {
        out += block.getTextContent();
        return;
      }
      for (const child of block.getChildren()) {
        out += $isFieldChipNode(child) ? refText(child) : child.getTextContent();
      }
    });
  return out;
}

/** Both renderings from one editor state; call inside editor.read/update. */
export function $serializeFormula(): FormulaSerialized {
  return {
    display: serialize((chip) => chip.getTextContent()),
    canonical: serialize((chip) => `[${chip.getFieldPath()}]`),
  };
}

/**
 * Offset into the display text of a position inside a node — a character
 * offset for text nodes, a child index for element nodes — following the
 * rendering $serializeFormula emits (blocks joined by "\n", chips as their
 * label text). Call inside editor.read/update.
 */
export function $displayOffsetOf(target: LexicalNode, offsetInNode: number): number {
  const blocks = $getRoot().getChildren();
  if ($isRootNode(target)) {
    let offset = 0;
    for (let b = 0; b < Math.min(offsetInNode, blocks.length); b++) {
      offset += blocks[b].getTextContentSize() + 1;
    }
    return Math.max(0, offset - (offsetInNode >= blocks.length ? 1 : 0));
  }
  let offset = 0;
  for (let b = 0; b < blocks.length; b++) {
    if (b > 0) {
      offset += 1;
    }
    const block = blocks[b];
    if (block.is(target)) {
      // An element point's offset counts children; a leaf's counts characters.
      return $isElementNode(block)
        ? offset +
            block
              .getChildren()
              .slice(0, offsetInNode)
              .reduce((n, child) => n + child.getTextContentSize(), 0)
        : offset + offsetInNode;
    }
    if (!$isElementNode(block)) {
      offset += block.getTextContentSize();
      continue;
    }
    for (const child of block.getChildren()) {
      if (child.is(target)) {
        return offset + offsetInNode;
      }
      offset += child.getTextContentSize();
    }
  }
  return offset;
}

export type FormulaSegment =
  { kind: 'text'; text: string } | { kind: 'chip'; path: string; label: string; variant: FieldChipVariant };

/**
 * Split stored canonical text into chip/text segments. Only refs whose path is
 * in the vocabulary become chips, labelled with their display path; unknown
 * refs — and the whole formula when it doesn't tokenize — stay as editable
 * text so nothing is silently dropped.
 */
export function segmentCanonicalFormula(formula: string, targets: Map<string, ChipTarget>): FormulaSegment[] {
  let tokens: Token[];
  try {
    tokens = tokenize(formula);
  } catch {
    return formula ? [{ kind: 'text', text: formula }] : [];
  }
  const segments: FormulaSegment[] = [];
  let cursor = 0;
  for (const token of tokens) {
    if (token.type !== 'field') {
      continue;
    }
    const target = targets.get(token.value);
    if (target == null) {
      continue;
    }
    if (token.position > cursor) {
      segments.push({ kind: 'text', text: formula.slice(cursor, token.position) });
    }
    segments.push({
      kind: 'chip',
      // The target's own path, not the ref as stored: a legacy alias lands on the record path.
      path: target.path,
      label: target.displayPath,
      variant: fieldChipVariant(target),
    });
    cursor = token.end;
  }
  if (cursor < formula.length) {
    segments.push({ kind: 'text', text: formula.slice(cursor) });
  }
  return segments;
}

/**
 * What the editor will emit for these segments — lets the modal seed its state
 * to match the editor's initial content without waiting for the first change.
 */
export function serializeSegments(segments: FormulaSegment[]): FormulaSerialized {
  let display = '';
  let canonical = '';
  for (const segment of segments) {
    if (segment.kind === 'chip') {
      display += fieldChipText(segment.label);
      canonical += `[${segment.path}]`;
    } else {
      display += segment.text;
      canonical += segment.text;
    }
  }
  return { display, canonical };
}

/** Replace the editor content with nodes built from stored canonical text; call inside editor.update. */
export function $initFromCanonical(formula: string, targets: Map<string, ChipTarget>): void {
  const root = $getRoot();
  root.clear();
  const paragraph = $createParagraphNode();
  for (const segment of segmentCanonicalFormula(formula, targets)) {
    if (segment.kind === 'chip') {
      paragraph.append($createFieldChipNode(segment.path, segment.label, segment.variant));
      continue;
    }
    segment.text.split('\n').forEach((line, i) => {
      if (i > 0) {
        paragraph.append($createLineBreakNode());
      }
      if (line) {
        paragraph.append($createTextNode(line));
      }
    });
  }
  root.append(paragraph);
}
